import * as THREE  from 'three'
import * as Hammer from 'hammerjs'
import TWEEN       from '@tweenjs/tween.js'
import Stats       from '../vendor/three/stats'
import {
  EffectComposer,
  RenderPass,
  EffectPass,
  OutlineEffect,
  SMAAEffect,
  BlendFunction, TextureEffect
}                  from 'postprocessing'

class Scene {

  constructor(viewportContainer) {
    if (!viewportContainer) {
      throw new Error('Viewport container for the scene is not specified.')
    }

    this.isLoaded = false
    this.renderLoopRuning = false
    this.viewportContainer = viewportContainer
    this.animationFrameId = null
    this.config = {}
    this.defaultConfig = window.Config.scenes.default
    this.scene = new THREE.Scene()
    this.renderer = null
    this.camera = null
    this.light = null
    this.controls = null
    this.clock = new THREE.Clock()
    this.renderComposerClock = new THREE.Clock()
    this.renderLoopClock = new THREE.Clock()
    this.mouseWheelClock = new THREE.Clock()
    this.mixers = []
    this.autoRotate = {
      delay: 5,
      enabled: false,
      paused: false,
      continueTimer: 0
    }
    this.interactiveObjects = []
    this.interactiveObjectsHighlighted = false
    this.hoveredObject = null

    this.isMouseDown = false
    this.isPointerMoved = false
    this.onMouseDownLon = 0
    this.onMouseDownLat = 0
    this.onPinchStartPointers = []
    this.lon = 0
    this.lat = 0
    this.phi = 0
    this.theta = 0

    this.loadingManager = new THREE.LoadingManager()
    this.pointerManager = new Hammer.Manager(this.viewportContainer)
    this.controlsEnabled = true

    // disposable resources
    this.geometries = []
    this.textures = []
    this.materials = []

    this.debugGui = null
  }

  getSceneConfig() {
    return this.config
  }

  setSceneConfig(config) {
    this.config = this.deepMerge({}, this.defaultConfig, config)
  }

  deepMerge(...sources) {
    let target = {}
    for (const source of sources) {
      if (source instanceof Array) {
        if (!(target instanceof Array)) {
          target = []
        }
        target = [...target, ...source]
      } else if (source instanceof Object) {
        for (let [key, value] of Object.entries(source)) {
          if (value instanceof Object && key in target) {
            value = this.deepMerge(target[key], value)
          }
          target = { ...target, [key]: value }
        }
      }
    }
    return target
  }

  setViewportContainer(container) {
    this.viewportContainer = container
  }

  setEmbeddedViewOptions(optionsObject) {
    this.embeddedViewOptions = optionsObject
  }

  init() {
    this.initLoader()
    this.initRenderer()
    this.initCamera()
    if (this.config.renderer.useComposer) {
      // it requires camera
      this.initRenderComposer()
    }
    this.initLights()
    this.initControls()
    this.loadModels()

    this.autoRotate.enabled = this.config.hasOwnProperty('autoRotate') ? this.config.autoRotate : false

    this.beforeRenderSceneLoop()
    this.viewportContainer.appendChild(this.renderer.domElement)

    this.initEvents()
    this.initEnvironment()

    if (window.Config.options.debug.showFps) {
      this.stats = new Stats()
      this.viewportContainer.appendChild(this.stats.dom)
    }
  }

  initLoader() {
    // let loaderDiv = document.createElement('div')
    // loaderDiv.className = 'loader-container'
    // this.viewportContainer.appendChild(loaderDiv)

    let manager = this.loadingManager
    manager.onStart = (url, itemsLoaded, itemsTotal) => {
      // console.log('Started loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.')
      this.isLoaded = false
      this.viewportContainer.dispatchEvent(new CustomEvent('sceneLoadStart'))
    }

    manager.onLoad = () => {
      // console.log('Loading complete!')
      // this.viewportContainer.removeChild(loaderDiv)
      this.isLoaded = true
      setTimeout(() => {
        // if nothing started to load nearest 100ms, consider loading is finished
        if (this.isLoaded) {
          this.triggerLoadedEvent()
        }
      }, 100)
    }

    manager.onProgress = (url, itemsLoaded, itemsTotal) => {
      // console.log('Loading file: ' + url + '.\nLoaded ' + itemsLoaded + ' of ' + itemsTotal + ' files.')
      this.viewportContainer.dispatchEvent(new CustomEvent('loadingProgress', {
        detail: {
          loaded: itemsLoaded,
          total: itemsTotal
        }
      }))
    }

    manager.onError = (url) => {
      // console.log('There was an error loading ' + url)
    }
  }

  triggerLoadedEvent() {
    this.onSceneIsLoaded()
    this.viewportContainer.dispatchEvent(new CustomEvent('sceneIsLoaded'))
  }

  onSceneIsLoaded() {
    this.startRenderLoop()
  }

  beforeRenderSceneLoop() {
  }

  initEvents() {
    this.onWindowResize = this.onWindowResize.bind(this)
    this.onViewportMouseMove = this.onViewportMouseMove.bind(this)
    this.onViewportWheel = this.onViewportWheel.bind(this)
    this.onDocumentMouseWheel = this.onDocumentMouseWheel.bind(this)

    window.addEventListener('resize', this.onWindowResize, false)

    // view control
    this.viewportContainer.addEventListener('mousemove', this.onViewportMouseMove, false)

    let tapEvent = new Hammer.Tap({ event: 'tap' }) // click
    let panEvent = new Hammer.Pan({ event: 'pan', pointers: 1 }) // pan camera view
    let pinchEvent = new Hammer.Pinch() // walk for mobiles

    this.pointerManager.add(tapEvent)
    this.pointerManager.add(panEvent)
    this.pointerManager.add(pinchEvent)

    this.pointerManager.on('panstart', this.onViewportPanStart.bind(this))
    this.pointerManager.on('panend', this.onViewportPanEnd.bind(this))
    this.pointerManager.on('pan', this.onViewportPan.bind(this))
    this.pointerManager.on('tap', this.onViewportTap.bind(this))
    this.pointerManager.on('pinchstart', this.onViewportPinchStart.bind(this))
    this.pointerManager.on('pinch', this.onViewportPinchMove.bind(this))

    // walk for wheel
    this.viewportContainer.addEventListener('wheel', this.onViewportWheel, false)
    this.renderer.domElement.addEventListener('wheel', this.onViewportWheel, false)

    // Prevent default window scroll to change page scale
    document.addEventListener('wheel', this.onDocumentMouseWheel, false)

    this.viewportContainer.classList.add('viewport-pan-ready')
  }

  unbindEvents() {
    window.removeEventListener('resize', this.onWindowResize)

    // view control
    this.viewportContainer.removeEventListener('mousemove', this.onViewportMouseMove)

    this.pointerManager.destroy()

    this.viewportContainer.removeEventListener('wheel', this.onViewportWheel, false)
    this.renderer.domElement.removeEventListener('wheel', this.onViewportWheel, false)

    // Prevent default window scroll to change page scale
    document.removeEventListener('wheel', this.onDocumentMouseWheel)

    this.viewportContainer.classList.remove('viewport-pan-ready')
  }

  onUserInteraction() {
    this.toggleAutoRotatePause(true, true)
  }

  onViewportTap(event) {
    this.onUserInteraction()

    this.mousePosition = this.getMousePosition(this.viewportContainer, event.center.x, event.center.y)

    let intersections = this.getInteractiveIntersections(this.mousePosition, this.interactiveObjects)

    let clickEvent
    if (intersections[0]) {
      let clickedObject = intersections[0]
      if (this.onInteractiveObjectClick(clickedObject) === false) {
        // block click event
        // may be clicked object requires user to be in specific position on click to trigger its interactive event
        return
      }

      if (this.hoveredObject) {
        this.emitInteractiveObjectLeave()
        this.hoveredObject = null
        this.viewportContainer.classList.remove('viewport-interactive-hover')
      }

      let detail = {
        mousePosition: this.mousePosition.toArray(),
        embedded: null,
        data: {
          title: clickedObject.userData.title,
          content: clickedObject.userData.content,
          styles: clickedObject.userData.styles
        }
      }

      if (clickedObject.userData.slotConfig) {
        detail.embedded = clickedObject.userData.slotConfig.content.embedded
      } else if (clickedObject.userData.objectViewConfig) {
        detail.embedded = window.Config.scenes.objects.find(
          itemConfig => itemConfig.id === clickedObject.userData.objectViewConfig.id
        )
      }

      clickEvent = new CustomEvent('interactiveObjectClick', { detail })
    } else {
      clickEvent = new CustomEvent('panoramaClick', {
        'detail': {
          mousePosition: this.mousePosition.toArray()
        }
      })
    }
    this.viewportContainer.dispatchEvent(clickEvent)
  }

  onInteractiveObjectClick(object) {
  }

  onViewportPanStart(event) {
    this.isMouseDown = true

    this.onMouseDownLon = this.lon
    this.onMouseDownLat = this.lat

    this.viewportContainer.classList.add('viewport-panning')
  }

  onViewportPanEnd(event) {
    this.isMouseDown = false

    this.viewportContainer.classList.remove('viewport-panning')
  }

  onViewportPan(event) {
    this.onUserInteraction()

    this.mousePosition = this.getMousePosition(this.viewportContainer, event.center.x, event.center.y)
    // trigger leave
    if (this.hoveredObject) {
      this.emitInteractiveObjectLeave()
      this.viewportContainer.classList.remove('viewport-interactive-hover')
      this.hoveredObject = null
    }

    this.lon = -event.deltaX * 0.1 + this.onMouseDownLon
    this.lat = event.deltaY * 0.1 + this.onMouseDownLat
  }

  onViewportPinchStart(event) {
    // should be assigned separately "event.pointers" is a reference and it values will be updated on next pinch event
    this.onPinchStartPointers[0] = event.pointers[0]
    this.onPinchStartPointers[1] = event.pointers[1]
  }

  onViewportPinchMove(event) {
    // separate pinch from two-fingers scroll

    let pointer0Vector = new THREE.Vector2(this.onPinchStartPointers[0].x - event.pointers[0].x, this.onPinchStartPointers[0].y - event.pointers[0].y)
    let pointer1Vector = new THREE.Vector2(this.onPinchStartPointers[1].x - event.pointers[1].x, this.onPinchStartPointers[1].y - event.pointers[1].y)
    let vectorDotProduct = pointer0Vector.dot(pointer1Vector)

    if (vectorDotProduct < 0) {
      this.onViewportPinchScale(event)
    } else {
      this.onViewportPanScroll(event)
    }
  }

  onViewportPinchScale(event) {
    this.onUserInteraction()
    let speed = 20
    let distance = 0

    if (event.scale < 1) {
      distance = -speed / event.scale
    } else {
      distance = speed * event.scale
    }

    this.onPointerScroll(distance)
  }

  onViewportPanScroll(event) {
    this.onUserInteraction()

    if (event.direction !== Hammer.DIRECTION_UP && event.direction !== Hammer.DIRECTION_DOWN) {
      return
    }

    let relativeSize = this.getMousePosition(this.viewportContainer, 0, event.distance)
    let distance = relativeSize.y * 100
    if (event.direction === Hammer.DIRECTION_DOWN) {
      distance *= -1
    }

    this.onPointerScroll(distance)
  }

  onViewportMouseMove(event) {
    if (!this.controlsEnabled) {
      return
    }

    this.isPointerMoved = true
    this.mousePosition = this.getMousePosition(this.viewportContainer, event.clientX, event.clientY)
  }

  onPointerScroll(delta) {
    // this.camera.fov = THREE.Math.clamp(this.camera.fov + delta * 0.05, 10, 75)
    // this.camera.updateProjectionMatrix()
  }

  emitInteractiveObjectLeave() {
    if (this.hoveredObject) {
      this.highlightInteractiveObject(this.hoveredObject, false, false)
    }

    // leave
    let event = new CustomEvent('interactiveObjectLeave', {
      detail: {
        data: {
          sceneId: this.hoveredObject.userData.sceneId,
          title: this.hoveredObject.userData.title,
          styles: this.hoveredObject.userData.styles
        }
      }
    })
    this.viewportContainer.dispatchEvent(event)
  }

  onInteractiveObjectHover(intersections) {
    this.highlightInteractiveObjects(this.interactiveObjectsHighlighted)

    if (intersections[0]) {
      if (this.hoveredObject && this.hoveredObject !== intersections[0]) {
        this.emitInteractiveObjectLeave()
        this.highlightInteractiveObject(this.hoveredObject, false, false)
      }

      this.hoveredObject = intersections[0]
      this.highlightInteractiveObject(this.hoveredObject, true, true)

      // hover
      // let embedded = intersections[0].userData.slotConfig? intersections[0].userData.slotConfig.content.embedded : null

      let event = new CustomEvent('interactiveObjectHover', {
        'detail': {
          mousePosition: this.mousePosition.toArray(),
          data: {
            // embedded: embedded,
            title: intersections[0].userData.title,
            styles: intersections[0].userData.styles
          }
        }
      })
      this.viewportContainer.dispatchEvent(event)
      this.viewportContainer.classList.add('viewport-interactive-hover')
    } else {
      if (this.hoveredObject) {
        this.emitInteractiveObjectLeave()
        this.hoveredObject = null
        this.viewportContainer.classList.remove('viewport-interactive-hover')
      }
    }
  }

  checkInteractiveObjectsHover() {
    let intersections = []
    if (this.controlsEnabled) {
      intersections = this.getInteractiveIntersections(this.mousePosition, this.interactiveObjects)
    }
    this.onInteractiveObjectHover(intersections)
  }

  onViewportWheel(event) {
    event.preventDefault()

    if (!this.controlsEnabled) {
      return
    }

    let lastUpdateDelta = this.mouseWheelClock.getDelta()
    if (lastUpdateDelta < 0.001) {
      return
    }

    this.onPointerScroll(event.deltaY)
  }

  onDocumentMouseWheel(event) {
    event.preventDefault()
    event.stopPropagation()
  }

  getInteractiveIntersections(point, objects) {
    let mouse = new THREE.Vector2((point.x * 2) - 1, -(point.y * 2) + 1)
    this.interactiveRaycaster.setFromCamera(mouse, this.camera)
    let intersections = this.interactiveRaycaster.intersectObjects(objects)

    let interactives = []
    intersections.map((intersection) => {
      if (intersection.object.name === 'glow') {
        return
      }

      let mainObject = this.getInteractiveObjectParent(intersection.object)

      if (mainObject) {
        interactives.push(mainObject)
      }
    })

    return interactives
  }

  getMousePosition(element, x, y) {
    let rect = element.getBoundingClientRect()
    return new THREE.Vector2((x - rect.left) / rect.width, (y - rect.top) / rect.height)
  }

  /* region Scene renderer */

  initRenderer() {
    let rendererConfig = this.getConfigRenderer()
    let rendererOptions = {
      autoClear: true,
      // physicallyCorrectLights: true,
      // toneMappingExposure: rendererConfig.toneMappingExposure,
      // toneMapping: THREE.ReinhardToneMapping,
      antialias: rendererConfig.antialias
    }
    if (rendererConfig.alpha) {
      rendererOptions.alpha = true
    }
    this.renderer = new THREE.WebGLRenderer(rendererOptions)

    this.renderer.shadowMap.enabled = rendererConfig.enableShadowMap
    if (this.renderer.shadowMap.enabled && rendererConfig.shadowMapType) {
      this.renderer.shadowMap.type = THREE[rendererConfig.shadowMapType]
    }
    this.renderer.setPixelRatio(window.devicePixelRatio)

    if (!rendererConfig.alpha && rendererConfig.clearColor) {
      this.renderer.setClearColor(rendererConfig.clearColor,1)
    }

    if (rendererConfig.size && rendererConfig.size.x && rendererConfig.size.y) {
      this.renderer.setSize(rendererConfig.size.x, rendererConfig.size.y)
    } else if (this.viewportContainer) {
      this.renderer.setSize(this.viewportContainer.offsetWidth, this.viewportContainer.offsetHeight)
    } else {
      this.renderer.setSize(window.innerWidth, window.innerHeight)
    }

    this.renderer.gammaInput = rendererConfig.gammaInput
    this.renderer.gammaOutput = rendererConfig.gammaOutput
  }

  initRenderComposer() {
    this.renderComposer = new EffectComposer(this.renderer)
    this.outlineEffect = new OutlineEffect(
      this.scene,
      this.camera,
      {
        blendFunction: BlendFunction.SCREEN,
        patternTexture: null,
        edgeStrength: 5.0,
        kernelSize: 2, // BlurPass parameter: effect.kernelSize = params.blurriness - 1
        pulseSpeed: 0.3,
        visibleEdgeColor: this.config.colors.glowBaseColor,
        hiddenEdgeColor: 0x22090a,
        blur: true,
        xRay: false
      }
    )

    const searchImage = new Image()
    const areaImage = new Image()
    searchImage.src = SMAAEffect.searchImageDataURL
    areaImage.src = SMAAEffect.areaImageDataURL

    const smaaEffect = new SMAAEffect(searchImage, areaImage)
    //smaaEffect.setEdgeDetectionThreshold(0.05)

    const textureEffect = new TextureEffect({
      blendFunction: BlendFunction.SKIP,
      texture: smaaEffect.renderTargetColorEdges.texture
    })

    const effectPass = new EffectPass(this.camera, smaaEffect, textureEffect, this.outlineEffect) // new BloomEffect()
    effectPass.renderToScreen = true

    const smaaPass = new EffectPass(this.camera, smaaEffect)
    const outlinePass = new EffectPass(this.camera, this.outlineEffect)
    const renderPass = new RenderPass(this.scene, this.camera)
    renderPass.renderToScreen = false
    smaaPass.renderToScreen = true

    // The outline effect uses mask textures which produce aliasing artifacts.
    this.renderComposer.addPass(renderPass)
    this.renderComposer.addPass(outlinePass)
    this.renderComposer.addPass(smaaPass)
  }

  outlineObject(object, doOutline = true) {
    if (object instanceof THREE.Group) {
      object.traverse((child) => {
        if (child.isMesh) {
          if (doOutline) {
            this.outlineEffect.selectObject(child)
          } else {
            this.outlineEffect.deselectObject(child)
          }
        }
      })
    } else {
      if (doOutline) {
        this.outlineEffect.selectObject(object)
      } else {
        this.outlineEffect.deselectObject(object)
      }
    }
  }

  getConfigRenderer() {
    return this.config.renderer || this.getConfigRendererDefaults()
  }

  setConfigRenderer(configRenderer) {
    this.config.renderer = configRenderer
  }

  getConfigRendererDefaults() {
    return {
      alpha: true,
      antialias: true,
      size: null,
      enableShadowMap: true,
      clearColor: null
    }
  }

  render() {
    if (this.stats) {
      this.stats.update()
    }
    if (this.renderer && this.scene && this.camera) {
      if (this.config.renderer.useComposer && this.renderComposer) {
        this.renderComposer.render(this.renderComposerClock.getDelta())
      } else {
        this.renderer.render(this.scene, this.camera)
      }
    }
  }

  /* endregion Scene renderer */

  /* region Scene camera */

  initCamera() {
    const cameraConfig = this.getConfigCamera()
    switch (cameraConfig.type) {
      case 'perspective':
      default:
        this.camera = new THREE.PerspectiveCamera(
          cameraConfig.fov,
          cameraConfig.aspect,
          cameraConfig.near,
          cameraConfig.far
        )
    }

    this.camera.position.set(cameraConfig.position.x, cameraConfig.position.y, cameraConfig.position.z)

    this.camera.target = new THREE.Vector3(0, 0, 0)

    if (cameraConfig.rotation) {
      this.lon = cameraConfig.rotation.lon
      this.lat = cameraConfig.rotation.lat
    }
  }

  getConfigCamera() {
    return Object.assign({}, this.getConfigCameraDefaults(), this.config.camera)
  }

  setConfigCamera(configCamera) {
    this.config.camera = configCamera
  }

  getConfigCameraDefaults() {
    return {
      type: 'perspective',
      fov: 50,
      aspect: this.getAspect(),
      near: 1,
      far: 2000,
      position: {
        x: 100,
        y: 100,
        z: 100
      }
    }
  }

  /* endregion Scene camera */

  /* region Scene controls */

  initControls() {
    // interactions
    this.interactiveRaycaster = new THREE.Raycaster()
    this.mousePosition = new THREE.Vector2()

    this.mousePositionOnDown = new THREE.Vector2()
    this.mousePositionOnUp = new THREE.Vector2()
  }

  disableControls() {
    this.isMouseDown = false
    this.controlsEnabled = false

    this.pointerManager.set({ enable: false })

    // trigger leave
    if (this.hoveredObject) {
      this.emitInteractiveObjectLeave()
      this.hoveredObject = null
      this.viewportContainer.classList.remove('viewport-interactive-hover')
    }
    this.viewportContainer.classList.remove('viewport-pan-ready')
  }

  enableControls() {
    this.controlsEnabled = true

    this.pointerManager.set({ enable: true })
    this.viewportContainer.classList.add('viewport-pan-ready')
  }

  getConfigControls() {
    return this.config.controls || this.getConfigControlsDefaults()
  }

  setConfigControls(configControls) {
    this.config.controls = configControls
  }

  getConfigControlsDefaults() {
    return {
      enableZoom: false,
      enablePan: false,
      enableDamping: true,
      rotateSpeed: 0.25,
      target: {
        x: 0,
        y: 100,
        z: 0
      }
    }
  }

  /* endregion Scene controls */

  /* region Scene lights */

  initLights() {
    const lightsConfig = this.getConfigLights()

    if (lightsConfig.length) {
      if (!this.scene) {
        throw new Error('Error scene light initialization. Scene is need to be initialized.')
      }

      lightsConfig.map(lightConfig => this.initLight(lightConfig))
    }
  }

  initLight(lightConfig, targetObject = null) {
    if (lightConfig.intensity === 0) {
      return
    }

    if (!targetObject) {
      targetObject = this.scene
    }

    let light = null

    switch (lightConfig.type) {
      case 'directional':
        light = new THREE.DirectionalLight(lightConfig.color, lightConfig.intensity)
        light.castShadow = lightConfig.castShadow

        if (lightConfig.castShadow && lightConfig.shadow) {
          //Set up shadow properties for the light
          if (lightConfig.shadow.mapSize) {
            light.shadow.mapSize.width = lightConfig.shadow.mapSize.width  // default
            light.shadow.mapSize.height = lightConfig.shadow.mapSize.height // default
          }

          if (lightConfig.shadow.camera) {
            light.shadow.camera.near = lightConfig.shadow.camera.near
            light.shadow.camera.far = lightConfig.shadow.camera.far

            light.shadow.camera.top = lightConfig.shadow.camera.top
            light.shadow.camera.bottom = lightConfig.shadow.camera.bottom
            light.shadow.camera.left = lightConfig.shadow.camera.left
            light.shadow.camera.right = lightConfig.shadow.camera.right
          }
        }

        break
      case 'spot':
        light = new THREE.SpotLight(lightConfig.color, lightConfig.intensity)

        console.log('setup spot light', light)

        light.castShadow = !!lightConfig.castShadow
        if (lightConfig.distance) {
          light.distance = lightConfig.distance
        }
        if (lightConfig.decay) {
          light.decay = lightConfig.decay
        }
        if (lightConfig.angle) {
          light.angle = THREE.Math.degToRad(lightConfig.angle)
        }
        break
      case 'point':
        light = new THREE.PointLight(lightConfig.color, lightConfig.intensity)
        light.castShadow = !!lightConfig.castShadow
        if (lightConfig.distance) {
          light.distance = lightConfig.distance
        }
        if (lightConfig.decay) {
          light.decay = lightConfig.decay
        }
        break
      case 'hemisphere':
        light = new THREE.HemisphereLight(lightConfig.color, lightConfig.groundColor, lightConfig.intensity)
        break
      case 'ambient':
        light = new THREE.AmbientLight(lightConfig.color, lightConfig.intensity)
        break
    }

    if (!light) {
      return
    }

    if (lightConfig.name) {
      light.name = lightConfig.name
    }
    if (lightConfig.position) {
      light.position.set(lightConfig.position.x, lightConfig.position.y, lightConfig.position.z)
    }
    targetObject.add(light)
    //this.scene.add(new THREE.CameraHelper(this.light.shadow.camera));

    if (window.Config.options.debug.debugLights) {
      this.showLightHelper(light)
    }
  }

  showLightHelper(light, show) {
    if (show) {
      let helper = null
      if (light instanceof THREE.DirectionalLight) {
        helper = new THREE.DirectionalLightHelper(light, light.intensity * 20, light.color)
        if (light.castShadow) {
          let shadowCameraHelper = new THREE.CameraHelper(light.shadow.camera)
          this.scene.add(shadowCameraHelper)
          light.userData._shadowCameraHelper = shadowCameraHelper
        }
      } else if (light instanceof THREE.SpotLight) {
        helper = new THREE.SpotLightHelper(light, light.color)
      } else if (light instanceof THREE.PointLight) {
        helper = new THREE.PointLightHelper(light, light.intensity * 20, light.color)
      } else if (light instanceof THREE.HemisphereLight) {
        helper = new THREE.HemisphereLightHelper(light, light.intensity * 20, light.color)
      }

      if (helper) {
        light.userData._helper = helper
        this.scene.add(helper)
      }
    } else {
      if (light.userData._helper) {
        this.scene.remove(light.userData._helper)
        light.userData._helper = null
      }
      if (light.userData._shadowCameraHelper) {
        this.scene.remove(light.userData._shadowCameraHelper)
        light.userData._shadowCameraHelper = null
      }
    }
  }

  getConfigLights() {
    return this.config.lights || this.getConfigLightsDefaults()
  }

  setConfigLights(configLights) {
    this.config.lights = configLights
  }

  getConfigLightsDefaults() {
    return []
  }

  /* endregion Scene lights */

  initEnvironment() {
  }

  switchEnvironmentScheme(scheme) {
  }

  /* region Scene grid */

  initGrid() {
    if (!this.scene) {
      throw new Error('Error scene grid initialization. Scene is need to be initialized.')
    }

    const gridConfig = this.getConfigGrid()
    let grid = new THREE.GridHelper(gridConfig.size, gridConfig.divisions, gridConfig.color1, gridConfig.color2)
    grid.material.opacity = gridConfig.material.opacity
    grid.material.transparent = gridConfig.material.transparent
    this.scene.add(grid)
  }

  getConfigGrid() {
    return this.config.grid || this.getConfigGridDefaults()
  }

  setConfigGrid(configGrid) {
    this.config.grid = configGrid
  }

  getConfigGridDefaults() {
    return {
      size: 2000,
      divisions: 20,
      color1: 0x000000,
      color2: 0x000000,
      material: {
        opacity: 0.2,
        transparent: true
      }
    }
  }


  /* endregion Scene grid */

  loadModels() {
  }

  renderSceneLoop(time) {
    this.sceneLoopUpdate(time, this.renderLoopClock.getDelta())
    this.animationFrameId = requestAnimationFrame((time) => {
      this.renderSceneLoop(time)
    })
  }

  startRenderLoop() {
    if (this.renderLoopRuning) {
      return
    }

    this.renderLoopRuning = true
    this.renderSceneLoop()
  }

  cancelSceneLoop() {
    cancelAnimationFrame(this.animationFrameId)
    this.renderLoopRuning = false
  }

  destroy() {
    this.cancelSceneLoop()
    this.unbindEvents()

    TWEEN.removeAll()

    if (this.stats && this.stats.dom && this.stats.dom.parentElement) {
      this.stats.dom.parentElement.removeChild(this.stats.dom)
    }

    this.interactiveObjects = []

    let stats = {
      disposedMaterials: 0,
      disposedTextures: 0,
      disposedGeometries: 0
    }

    this.scene.traverse(child => {
      if (!child.material) {
        return
      }

      if (child.material.length) {
        child.material.map(material => {
          this._disposeMaterial(material, stats)
        })
      } else {
        this._disposeMaterial(child.material, stats)
      }
    })


    this.scene.children.map(object => this.scene.remove(object))

    let stats2 = {
      disposedMaterials: 0,
      disposedTextures: 0,
      disposedGeometries: 0
    }

    this.materials.map(material => {
      this._disposeMaterial(material, stats2)
    })
    this.materials = []

    this.textures.map(texture => {
      this._disposeTexture(texture, stats)
    })
    this.textures = []

    this.geometries.map(geometry => {
      geometry.dispose()
      stats2.disposedGeometries++
    })
    this.geometries = []

    // console.info('disposedMaterials %f (%f + %f)', stats.disposedMaterials+stats2.disposedMaterials, stats.disposedMaterials, stats2.disposedMaterials)
    // console.info('disposedTextures %f (%f + %f)', stats.disposedTextures+stats2.disposedTextures, stats.disposedTextures, stats2.disposedTextures)
    // console.info('disposedGeometries', stats2.disposedGeometries)

    if (this.renderer.domElement.parentElement) {
      this.renderer.domElement.parentElement.removeChild(this.renderer.domElement)
    }

    this.renderer.dispose()
    this.renderer.forceContextLoss()
    this.renderer.context = null
    this.renderer.domElement = null
    this.renderer = null
    this.camera = null
    this.scene = null
    this.mixers = []
    this.pointerManager = null
  }

  _disposeMaterial(material, stats) {
    if (material.isDisposed) {
      return
    }

    if (material.map) {
      this._disposeTexture(material.map)
    }

    material.isDisposed = true
    material.dispose()

    if (stats) {
      stats.disposedMaterials++
    }
  }

  _disposeTexture(texture, stats) {
    if (!texture || typeof texture.dispose !== 'function') {
      return
    }

    if (texture.isDisposed) {
      return
    }

    texture.dispose()
    texture.isDisposed = true

    if (texture instanceof THREE.VideoTexture) {
      texture.image.pause()
      texture.image.src = ''
      texture.image.load()
      delete texture.image
    }

    if (stats) {
      stats.disposedTextures++
    }
  }

  sceneLoopUpdate(time) {
    if (this.autoRotate.enabled && !this.autoRotate.paused) {
      this.lon += 0.1
    }

    if (this.lat !== this._processedLat || this.lon !== this._processedLon) {
      if (Math.abs(this.lon) >= 360) {
        this.lon = this.lon % 360
      }

      this.lat = Math.max(-85, Math.min(85, this.lat))

      if (this.camera) {
        this.phi = THREE.Math.degToRad(90 - this.lat)
        this.theta = THREE.Math.degToRad(this.lon)

        this.camera.target.copy(this.camera.position)

        let directionVector = new THREE.Vector3()
        directionVector.x = 500 * Math.sin(this.phi) * Math.cos(this.theta)
        directionVector.y = 500 * Math.cos(this.phi)
        directionVector.z = 500 * Math.sin(this.phi) * Math.sin(this.theta)

        this.camera.target.add(directionVector)

        this.camera.lookAt(this.camera.target)

        this._processedLat = this.lat
        this._processedLon = this.lon
      }

      this.sendMovementDataEvent()
    }

    TWEEN.update(time)

    // recheck interactive objects hover after camera is rotated
    let unprocessedPointerMove = !this.isMouseDown && this.isPointerMoved
    let autorotateActive = this.autoRotate.enabled && !this.autoRotate.paused
    if (unprocessedPointerMove || autorotateActive) {
      this.isPointerMoved = false

      this.checkInteractiveObjectsHover()
    }

    let glowCycle = 0.2 + 0.2 * Math.sin(Math.PI + time / 300)
    this.interactiveObjects.map(object => {
      let mainObject = this.getInteractiveObjectParent(object)
      let highlightValue = mainObject.userData.glowValue ? mainObject.userData.glowValue + glowCycle : 0
      if (mainObject.userData.glowType === 'outline') {
        highlightValue = mainObject.userData.glowValue > 0 ? 1 : 0
      }

      this._highlightInteractiveObject(mainObject, highlightValue)
    })

    this.render()
  }

  gatherMovementData(data) {
    data.lat = this.lat
    data.lon = this.lon
  }

  sendMovementDataEvent() {
    let data = {}

    this.gatherMovementData(data)

    let event = new CustomEvent('movement', {
      detail: { data }
    })
    this.viewportContainer.dispatchEvent(event)
  }

  getInteractiveObjectParent(object) {
    if (!object)
      return

    if (object.isSprite) {
      object.parent.userData.isSpriteGroup = true
      return object.parent
    } else if (object.userData.interactiveParent) {
      // if (object.userData.interactiveParent.parent && !(object.userData.interactiveParent.parent instanceof THREE.Scene)) {
      //   return object.userData.interactiveParent.parent
      // } else {
      return object.userData.interactiveParent
      // }
    }
  }

  highlightInteractiveObjects(highlight = true, hovered = false) {
    this.interactiveObjects.map(object => {
      this.highlightInteractiveObject(this.getInteractiveObjectParent(object), highlight, hovered)
    })
  }

  highlightInteractiveObject(object, highlight, hovered) {
    object.userData.glowValue = (hovered ? 0.6 : 0.3) * highlight
    // this._highlightInteractiveObject(object, highlight ? value : 0)
  }

  _highlightInteractiveObject(object, highlightValue) {
    if (!object.userData.glowType) {
      // TODO: add warning to console or logger
      return
    }
    if (object.userData.processedGlowValue === highlightValue) {
      // if value is not changed - skip update
      return
    }
    object.userData.processedGlowValue = highlightValue

    if (object.userData.glowType === 'box') {
      let glow = object.getObjectByName('glow')
      if (!highlightValue) {
        if (glow) {
          object.remove(glow)
        }
        return
      } else {
        if (!glow) {
          glow = this.createGlowBox(object.userData.glowBoxSize.x, object.userData.glowBoxSize.y, 10)
          glow.name = 'glow'
          if (object.userData.glowBoxPosition) {
            glow.position.set(
              object.userData.glowBoxPosition.x,
              object.userData.glowBoxPosition.y,
              object.userData.glowBoxPosition.z
            )
          } else {
            glow.position.z = -0.1
          }
          glow.userData.skipInteractions = true
          object.add(glow)
        }

        glow.material.opacity = highlightValue
      }
    } else if (object.userData.glowType === 'outline') {
      this.outlineObject(object, highlightValue > 0)
    }
  }

  createGlowBox(width, height, glowSize = 10) {
    let baseColor = new THREE.Color(this.config.colors.glowBoxBaseColor)
    let glowPlaneGeometry = new THREE.PlaneBufferGeometry(glowSize * 3, glowSize * 3, 3, 3)
    // expand plane vertices to match image size + glow size
    /*
    vertices map:
    0 1 2 3
    4 5 6 7
    8 9 A B
    C D E F
     */
    let verticesOffsetY = height / 2 - glowSize / 2
    let verticesOffsetX = width / 2 - glowSize / 2
    let vertices = glowPlaneGeometry.attributes.position.array
    for (let i = 0; i < 4; i++) {
      // up/down
      let row
      let col = i
      row = 0
      vertices[row * 12 + col * 3 + 1] += verticesOffsetY // first row
      row = 1
      vertices[row * 12 + col * 3 + 1] += verticesOffsetY // second row

      row = 2
      vertices[row * 12 + col * 3 + 1] -= verticesOffsetY // 3 row
      row = 3
      vertices[row * 12 + col * 3 + 1] -= verticesOffsetY // 4 row

      // left / right
      row = i
      col = 0
      vertices[row * 12 + col * 3] -= verticesOffsetX
      col = 1
      vertices[row * 12 + col * 3] -= verticesOffsetX

      col = 2
      vertices[row * 12 + col * 3] += verticesOffsetX
      col = 3
      vertices[row * 12 + col * 3] += verticesOffsetX
    }
    glowPlaneGeometry.attributes.position.needsUpdate = true

    if (!this._glowTexture) {
      this._glowTexture = new THREE.TextureLoader().load('scenes/helpers/glow-corner.png')
    }

    this._glowTexture.wrapS = THREE.MirroredRepeatWrapping
    this._glowTexture.wrapT = THREE.MirroredRepeatWrapping
    this._glowTexture.repeat.x = 2
    this._glowTexture.repeat.y = 2
    this._glowTexture.offset.y = 1

    let glowMaterial = new THREE.MeshBasicMaterial({
      color: baseColor,
      map: this._glowTexture,
      transparent: true,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -0.75,
      polygonOffsetUnits: -5,
      // blending: THREE.AdditiveBlending
    })
    return new THREE.Mesh(glowPlaneGeometry, glowMaterial)
  }

  getAspect(fromViewportContainer = true) {
    let size = this.getViewportSize(fromViewportContainer)
    return size.width / size.height
  }

  getViewportSize(fromViewportContainer = true) {
    return !fromViewportContainer
      ? { width: window.innerWidth, height: window.innerHeight }
      : { width: this.viewportContainer.clientWidth, height: this.viewportContainer.clientHeight }
  }

  toggleAutoRotateEnabled(value) {
    this.autoRotate.enabled = value
  }

  toggleAutoRotatePause(pause, autoResume = false) {
    this.autoRotate.paused = pause

    if (this.autoRotate.continueTimer) {
      clearTimeout(this.autoRotate.continueTimer)
    }

    if (autoResume) {
      this.autoRotate.continueTimer = setTimeout(() => {
        this.toggleAutoRotatePause(false)
      }, this.autoRotate.delay * 1000)
    }
  }

  setHighlightInteractiveObjects(value) {
    this.interactiveObjectsHighlighted = value
    this.highlightInteractiveObjects(value)
  }

  updateSize() {
    let size = this.getViewportSize()
    this.camera.aspect = this.getAspect()
    this.camera.updateProjectionMatrix()
    this.renderer.setSize(size.width, size.height)
    if (this.config.renderer.useComposer && this.renderComposer) {
      this.renderComposer.setSize( size.width, size.height )
    }
  }

  onWindowResize() {
    this.updateSize()
  }
}

export default Scene
